/**
 * The server's push channel.
 *
 * Four things the kiosk must not poll for: a catalogue that changed, a branch
 * that closed, a QPay invoice that settled, and a support-triggered reload. The
 * old app polled `getBranchStatus`, polled QPay, and checked for updates on an
 * hourly timer.
 */
import { API_BASE } from './client'

export type ServerEvent =
  | { type: 'catalogue-changed'; revision: string }
  | { type: 'branch-status'; isClosed: boolean }
  | { type: 'payment-settled'; orderId: string; paymentId: string; reference: string | null }
  | { type: 'payment-failed'; orderId: string; paymentId: string; reason: string }
  | { type: 'reload-requested'; reason: string }
  /** The console asked this kiosk for a diagnostic bundle. */
  | { type: 'report-requested' }
  /** The console asked this kiosk to print its test slip and report what happened. */
  | { type: 'test-print-requested' }
  | { type: 'shift-changed'; shiftId: string | null }
  /** This shop's own record changed in the console. Re-read `branch.config`. */
  | { type: 'branch-config-changed' }
  /**
   * This shop's receipt template changed — its own, not the chain's.
   *
   * A chain-wide save sends nothing: two thousand messages for a footer change,
   * when every kiosk re-checks its revision on reconnect anyway.
   */
  | { type: 'receipt-template-changed' }
  /**
   * The console wants one arrangement printed here, once, to see what comes out.
   *
   * The id and never the document: the server broadcasts through Postgres
   * `NOTIFY`, capped at 8000 bytes, and a template carries a base64 logo. The
   * page pulls it with `receiptTemplate.proof`, which is also what claims it —
   * two sockets for one kiosk must not both print.
   *
   * Distinct from `test-print-requested`, which prints what this shop has
   * **saved**. This one is a draft nobody has saved, which is the only moment
   * where finding out what the printer does costs nothing.
   */
  | { type: 'receipt-proof-requested'; proofId: string }

type Handler = (event: ServerEvent) => void

/**
 * A socket that reconnects, with backoff and a ceiling.
 *
 * The ceiling matters more than the backoff: a fleet of kiosks whose uplink
 * came back at the same moment must not all reconnect in the same second, so
 * the delay carries jitter.
 */
export class ServerSocket {
  #socket: WebSocket | null = null
  #handlers = new Set<Handler>()
  #attempt = 0
  #closed = false
  #timer: ReturnType<typeof setTimeout> | null = null
  #token: string

  constructor(token: string) {
    this.#token = token
  }

  on(handler: Handler): () => void {
    this.#handlers.add(handler)
    return () => this.#handlers.delete(handler)
  }

  get connected(): boolean {
    return this.#socket?.readyState === WebSocket.OPEN
  }

  connect(): void {
    if (this.#closed) return
    const url = new URL(`${API_BASE}/socket`)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    // A browser WebSocket cannot set a header, so the token goes in the query.
    // It is the one place it appears in a URL; the server logs a redacted path.
    url.searchParams.set('token', this.#token)

    const socket = new WebSocket(url)
    this.#socket = socket

    socket.onopen = () => {
      this.#attempt = 0
    }

    socket.onmessage = (message) => {
      try {
        const event = JSON.parse(String(message.data)) as ServerEvent
        for (const handler of this.#handlers) handler(event)
      } catch {
        // A frame we cannot parse is a version skew, not a reason to drop the
        // connection everything else is arriving on.
      }
    }

    socket.onclose = () => {
      // A socket this instance has already replaced still fires its `close`.
      // Acting on it nulls the live socket and starts a second reconnect
      // chain running beside the first, each with its own backoff.
      if (this.#socket !== socket) return
      this.#socket = null
      this.#scheduleReconnect()
    }

    /*
     * `error` is always followed by `close`, so there was nothing for this
     * handler to do but make noise.
     *
     * It called `socket.close()`, and a refused connection errors while the
     * socket is still CONNECTING — which is the browser's «WebSocket is closed
     * before the connection is established», logged on every reconnect attempt
     * for as long as the server is down. The reconnect was never this
     * handler's: `onclose` above schedules it, and the browser fires `close`
     * whether or not anybody asks.
     */
    socket.onerror = () => {}
  }

  #scheduleReconnect(): void {
    if (this.#closed) return
    this.#attempt += 1
    const base = Math.min(30_000, 500 * 2 ** Math.min(this.#attempt, 6))
    const jitter = Math.random() * base * 0.5
    this.#timer = setTimeout(() => this.connect(), base + jitter)
  }

  close(): void {
    this.#closed = true
    if (this.#timer !== null) clearTimeout(this.#timer)
    this.#socket?.close()
    this.#socket = null
  }
}
